import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import BlogAdmin from "@/components/BlogAdmin";
import CatalogueAdmin from "@/components/CatalogueAdmin";
import UsersAdmin from "@/components/UsersAdmin";
import { ArrowsClockwise } from "@phosphor-icons/react";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "catalogue", label: "Catalogue" },
  { id: "users", label: "Users" },
  { id: "blog", label: "Blog" },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [logs, setLogs] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState("");

  const refresh = async () => {
    const [s, o, l] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/orders"),
      api.get("/admin/sync/logs"),
    ]);
    setStats(s.data); setOrders(o.data); setLogs(l.data);
  };

  useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);

  const triggerSync = async () => {
    setSyncing(true); setMsg("Syncing from salelink.co.nz…");
    try {
      const r = await api.post("/admin/sync");
      setMsg(`Synced ${r.data.synced} products.`);
      await refresh();
    } catch (e) {
      setMsg("Sync failed: " + (e.response?.data?.detail || e.message));
    } finally { setSyncing(false); }
  };

  if (loading) return <div className="p-12 text-center">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  return (
    <div>
      <div className="border-b border-zinc-900 px-6 md:px-12 py-8 flex flex-col md:flex-row justify-between gap-4">
        <div>
          <div className="tag mb-3 bg-[#0055ff] text-white border-[#0055ff]">Control Room</div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter uppercase font-[Outfit]">Admin Dashboard</h1>
        </div>
        <button data-testid="admin-sync-btn" onClick={triggerSync} disabled={syncing} className="btn-brutal btn-blue self-start">
          <ArrowsClockwise size={16} weight="bold" className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? "Syncing…" : "Sync Now"}
        </button>
      </div>
      {msg && <div className="px-6 md:px-12 py-3 border-b border-zinc-900 bg-yellow-100 text-xs uppercase tracking-widest font-bold font-[Outfit]">{msg}</div>}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 grid-borders border-l border-zinc-900 border-b">
          {[
            { l: "Products", v: stats.products },
            { l: "Orders", v: stats.orders_total },
            { l: "Paid Orders", v: stats.orders_paid },
            { l: "Revenue NZD", v: `$${stats.revenue_nzd.toFixed(2)}` },
            { l: "Users", v: stats.users },
          ].map(s => (
            <div key={s.l} className="p-6">
              <div className="text-xs uppercase tracking-widest font-bold font-[Outfit] text-zinc-500">{s.l}</div>
              <div className="text-4xl font-black font-[Outfit] tracking-tighter mt-2">{s.v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="border-b border-zinc-900 px-6 md:px-12 flex gap-0 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-6 py-4 text-xs uppercase tracking-widest font-bold font-[Outfit] border-r border-zinc-900 first:border-l whitespace-nowrap ${tab === t.id ? "bg-zinc-900 text-white" : "hover:bg-zinc-100"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          <div className="px-6 md:px-12 py-8">
            <h2 className="text-2xl font-black tracking-tighter uppercase font-[Outfit] mb-4">Recent Orders</h2>
            <div className="border border-zinc-900 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900 text-white text-xs uppercase tracking-widest font-[Outfit]">
                  <tr>
                    <th className="text-left px-4 py-3">Order</th>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Customer</th>
                    <th className="text-left px-4 py-3">Items</th>
                    <th className="text-left px-4 py-3">Total</th>
                    <th className="text-left px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr><td colSpan="6" className="px-4 py-6 text-center text-zinc-500">No orders yet</td></tr>
                  ) : orders.map(o => (
                    <tr key={o.id} className="border-t border-zinc-200">
                      <td className="px-4 py-3 font-mono text-xs">#{o.id.slice(0, 8)}</td>
                      <td className="px-4 py-3">{new Date(o.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3">{o.customer_email || "Guest"}</td>
                      <td className="px-4 py-3">{o.items.length}</td>
                      <td className="px-4 py-3 font-bold">${o.total.toFixed(2)}</td>
                      <td className="px-4 py-3"><span className={`tag ${o.payment_status === "paid" ? "bg-[#0055ff] text-white border-[#0055ff]" : ""}`}>{o.payment_status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {logs.length > 0 && (
            <div className="px-6 md:px-12 py-8 border-t border-zinc-900">
              <h2 className="text-2xl font-black tracking-tighter uppercase font-[Outfit] mb-4">Sync History</h2>
              <ul className="border border-zinc-900 divide-y divide-zinc-200">
                {logs.map(l => (
                  <li key={l.id} className="px-4 py-2 text-sm flex justify-between">
                    <span>{new Date(l.finished).toLocaleString()}</span>
                    <span className="font-bold">{l.synced} products</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {tab === "catalogue" && <CatalogueAdmin />}
      {tab === "users" && <UsersAdmin />}
      {tab === "blog" && <BlogAdmin />}
    </div>
  );
}
